/**
 * Dónde va el panel del caso —Copiloto IA, Documentos, Ficha— en la bandeja y
 * en En vivo: en una columna fija a la derecha, o en un cajón que se abre con
 * un botón (DEF-031).
 *
 * POR QUÉ EXISTE (QA de TEST, 05-oct-2026)
 * ----------------------------------------
 * Reproducido a 1100×600: el documento medía 1340×800 y la app 600 de alto. Al
 * bajar se perdían los 215 px de arriba —el encabezado— y abajo quedaba una
 * banda blanca de 215 px; además había scroll horizontal. `#root` pedía un
 * ancho mínimo de 1340 px y un alto mínimo de 800, y el Layout medía la
 * ventana: dos reglas de alto que no coincidían. Y una laptop de 1366 px con
 * Windows al 125 % deja ~1090 px CSS: scroll lateral incluso maximizada.
 *
 * Ruben aprobó bajar el mínimo a 1024 px (05-oct-2026). A ese ancho no siempre
 * entran las cuatro columnas —sidebar, lista, chat y panel—, así que cuando no
 * entran el panel pasa a un cajón, y el chat conserva el ancho que necesita.
 *
 * CON EL ANCHO REAL DEL SIDEBAR (DEF-052, QA de TEST 05-oct-2026)
 * ---------------------------------------------------------------
 * La primera versión decidía siempre con el sidebar EXPANDIDO (el peor caso) y
 * con un piso fijo en `xl`: con el menú colapsado sobraban 172 px y el panel
 * igual se plegaba, y con el menú expandido se plegaba debajo de 1280 aunque
 * el chat tuviera su mínimo. QA lo vio plegarse en una portátil común. Hoy la
 * cuenta usa el ancho del sidebar que está en pantalla y el ancho de cada
 * columna en cada tramo (debajo de `xl` y desde `xl`), y solo pide que al chat
 * le quede su mínimo. Umbrales efectivos, en px CSS de ventana:
 *
 *                          bandeja   En vivo
 *     sidebar expandido      1136      1344
 *     sidebar colapsado       964      1068
 *
 * DESDE 768 (DEF-031, decisión de Ruben del 07-oct-2026)
 * -----------------------------------------------------
 * QA midió scroll lateral a 1165 px físicos, que con Windows al 125 % son 932
 * px CSS: debajo del mínimo de 1024, así que `#root` empujaba la página y el
 * sidebar —menú, usuario, salir— quedaba fuera de la vista. Hoy el mínimo es
 * 768, y para que la fila entre ahí pasan dos cosas, las dos acá:
 *
 * - **El sidebar se colapsa solo** debajo de `ANCHO_SIDEBAR_AUTOMATICO` (940):
 *   expandido no entra la fila más exigente, la de En vivo en cajón. Quien lo
 *   abre o lo cierra a mano manda mientras la ventana siga angosta; al volver
 *   a ensancharse se retoma lo que eligió con la ventana ancha, y la próxima
 *   vez que se angoste vuelve a colapsarse solo (`sidebarColapsado`).
 * - **En cajón, el chat de En vivo cede a 412** (`CHAT_MINIMO_EN_CAJON`): con
 *   444, 64 + 248 + 444 + 44 = 800 no entra en 768. Su cabecera se acomoda en
 *   dos renglones. En columna sigue pidiendo 444: los umbrales de arriba no
 *   cambian.
 *
 * El área de las demás pantallas nunca baja de 704 px (768 − 64 = 940 − 236).
 *
 * Puro y sin imports: el test lo transpila y lo corre en Node
 * (`tests/unit/test_qa_test_05oct_ancho_minimo.py`,
 * `tests/unit/test_qa_test_06oct_portal.py`,
 * `tests/unit/test_qa_test_07oct_portal.py`), y las guardas sobre la fuente
 * cruzan cada constante con la clase de su columna. Lo cablean
 * `useDisposicion.ts`, que lee el estado del sidebar del Layout, y el Layout.
 */

/** El ancho mínimo del portal: `#root` en `index.css`. Debajo, scroll lateral.
 *  Era 1024 (05-oct-2026); 768 desde el 07-oct-2026. */
export const ANCHO_MINIMO = 768

/** El breakpoint `xl` de Tailwind: desde acá la lista y el panel se ensanchan. */
export const XL = 1280

// El ancho de cada columna, el mismo que su clase (lo cruza el test).
/** `Sidebar.tsx`, expandido (`w-[236px]`). */
export const ANCHO_SIDEBAR = 236
/** `Sidebar.tsx`, colapsado (`w-16`). */
export const ANCHO_SIDEBAR_COLAPSADO = 64
/** `ConversationList.tsx`: `w-[248px]`, y `xl:w-[292px]`. */
export const ANCHO_LISTA = 248
export const ANCHO_LISTA_XL = 292
/** `ContextPanel.tsx` en columna: `w-[312px]`, y `xl:w-[372px]`. En el cajón
 *  mide siempre 372 (`w-[372px]`). */
export const ANCHO_PANEL = 312
export const ANCHO_PANEL_XL = 372
/** El riel del botón que abre el cajón (`w-11`). */
export const ANCHO_RIEL = 44

/** Lo que necesita el chat para que su cabecera no se monte: el de la bandeja
 *  (`min-w-[340px]`) y el de En vivo, cuya cabecera lleva el chip largo de
 *  «Solo lectura · asignada a … · conduce el agente IA» (`min-w-[444px]`). */
export const CHAT_MINIMO_BANDEJA = 340
export const CHAT_MINIMO_EN_VIVO = 444

/** Lo que el chat puede pedir con el panel en cajón: lo que queda a
 *  `ANCHO_MINIMO` con el sidebar colapsado, la lista y el riel
 *  (768 − 64 − 248 − 44). La clase del chat de En vivo (`min-w-[412px]`). */
export const CHAT_MINIMO_EN_CAJON = 412

/** Debajo de este ancho de ventana el sidebar se colapsa solo: la fila más
 *  exigente con el sidebar expandido, En vivo en cajón (236 + 248 + 412 + 44). */
export const ANCHO_SIDEBAR_AUTOMATICO = 940

export type Disposicion = 'columna' | 'cajon'

/** El ancho del sidebar que está en pantalla. */
export function anchoDelSidebar(colapsado: boolean): number {
  return colapsado ? ANCHO_SIDEBAR_COLAPSADO : ANCHO_SIDEBAR
}

/** ¿La ventana es tan angosta que el sidebar se colapsa solo? */
export function ventanaAngosta(ventana: number): boolean {
  return ventana < ANCHO_SIDEBAR_AUTOMATICO
}

/**
 * ¿El sidebar va colapsado?
 *
 * `elegido`: lo que quien mira eligió con la ventana ancha (expandido de
 * entrada). `enAngosto`: lo que eligió mientras la ventana está angosta, o
 * `null` si no tocó nada — ahí se colapsa solo. El Layout vuelve
 * `enAngosto` a `null` cuando la ventana se ensancha: cada angostamiento
 * empieza colapsado, que es lo que hace entrar la fila.
 */
export function sidebarColapsado(angosta: boolean, elegido: boolean,
                                 enAngosto: boolean | null): boolean {
  return angosta ? (enAngosto ?? true) : elegido
}

/** El mínimo del chat de una pantalla cuando el panel está en cajón. */
export function chatMinimoEnCajon(chatMinimo: number = CHAT_MINIMO_BANDEJA): number {
  return Math.min(chatMinimo, CHAT_MINIMO_EN_CAJON)
}

export function anchoDeLaLista(ventana: number): number {
  return ventana >= XL ? ANCHO_LISTA_XL : ANCHO_LISTA
}

/** El panel en columna: 312 debajo de `xl`, 372 desde `xl`. */
export function anchoDelPanel(ventana: number): number {
  return ventana >= XL ? ANCHO_PANEL_XL : ANCHO_PANEL
}

/**
 * Desde qué ancho de ventana el panel va en columna, con este sidebar.
 *
 * En `xl` la lista y el panel se ensanchan 104 px de golpe, así que «entra o
 * no» no crece parejo con la ventana: con el sidebar expandido En vivo entra
 * de 1240 a 1279 (columnas angostas) y deja de entrar en 1280. Para que el
 * panel no se pliegue al AGRANDAR la ventana, el tramo de debajo de `xl` vale
 * solo si desde `xl` también entra; si no, el umbral es el de las columnas
 * anchas.
 */
export function umbralDeColumna(chatMinimo: number = CHAT_MINIMO_BANDEJA,
                                sidebar: number = ANCHO_SIDEBAR): number {
  const debajoDeXl = sidebar + ANCHO_LISTA + ANCHO_PANEL + chatMinimo
  const desdeXl = Math.max(XL, sidebar + ANCHO_LISTA_XL + ANCHO_PANEL_XL + chatMinimo)
  return desdeXl === XL ? Math.min(debajoDeXl, XL) : desdeXl
}

/**
 * Columna si a la ventana le alcanza para el sidebar que está en pantalla, la
 * lista, el panel y el mínimo del chat; si no, cajón.
 *
 * El mínimo del chat es por pantalla: a 1280 con el sidebar expandido la
 * bandeja entra (236 + 292 + 340 + 372 = 1240) y En vivo no (… + 444 = 1344).
 * Sin eso, el panel quedaba recortado por la derecha, sin scroll que lo
 * alcanzara.
 */
export function disposicionDelPanel(ventana: number,
                                    chatMinimo: number = CHAT_MINIMO_BANDEJA,
                                    sidebar: number = ANCHO_SIDEBAR): Disposicion {
  return ventana >= umbralDeColumna(chatMinimo, sidebar) ? 'columna' : 'cajon'
}

/** Lo que ocupa la fila con cada columna en su mínimo. Si pasa del ancho de la
 *  ventana, hay scroll lateral o una columna recortada: el test lo recorre de
 *  `ANCHO_MINIMO` en adelante, con el sidebar que pone `sidebarColapsado`. */
export function anchoMinimoDeLaFila(ventana: number,
                                    chatMinimo: number = CHAT_MINIMO_BANDEJA,
                                    sidebar: number = ANCHO_SIDEBAR): number {
  const enColumna = disposicionDelPanel(ventana, chatMinimo, sidebar) === 'columna'
  return sidebar + anchoDeLaLista(ventana)
    + (enColumna ? chatMinimo + anchoDelPanel(ventana)
                 : chatMinimoEnCajon(chatMinimo) + ANCHO_RIEL)
}

/**
 * ¿Esta tecla cierra el cajón?
 *
 * Escape, con el cajón abierto, y solo si no hay un diálogo modal encima —el
 * visor de un documento, una confirmación—: ahí Escape es de ése, y cerrar los
 * dos de un golpe deja al asesor fuera del documento que estaba mirando.
 */
export function escapeCierraElCajon(tecla: string, abierto: boolean,
                                    hayDialogoModal: boolean): boolean {
  return tecla === 'Escape' && abierto && !hayDialogoModal
}
